import { useState } from "react";
import { Helmet } from "react-helmet";
import { Tag, Button, Input, Select, Space, Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import trans, { isRTL } from "../../utils";
import AntdTable from "../antd/Table/Table";
import BreadCrumb from "../Header/SecondHeader";
import AccessDenied from "../AccessDenied";
import usePermission from "../../hooks/usePermission";
import { formatDateString } from "../../JsHelpers/DateFormat";
import {
  useGetServiceRequestCancellationsQuery,
  useApproveCancellationRequestMutation,
  useRejectCancellationRequestMutation,
} from "../../Redux/service/Requests/Request";
import { toast } from "react-toastify";

const STATUS_COLORS = {
  pending: "orange",
  approved: "green",
  rejected: "red",
};

const STATUS_OPTIONS = [
  { label: trans("Pending"), value: "pending" },
  { label: trans("Approved"), value: "approved" },
  { label: trans("Rejected"), value: "rejected" },
];

const CancelationRequests = () => {
  const { view, edit: canEdit } = usePermission("CANCELATION_REQUESTS");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [sort, setSort] = useState({ sortBy: "requestedAt", sortOrder: "desc" });

  const { data, isFetching, refetch } = useGetServiceRequestCancellationsQuery(
    {
      page: currentPage,
      limit,
      search,
      status: statusFilter.length ? statusFilter[0] : undefined,
      sortby: sort.sortBy,
      order: sort.sortOrder,
    },
    { refetchOnMountOrArgChange: true },
  );

  const [approveRequest] = useApproveCancellationRequestMutation();
  const [rejectRequest] = useRejectCancellationRequestMutation();

  const handleTableChange = (_pagination, _filters, sorter) => {
    if (sorter?.order) {
      setSort({
        sortBy: sorter.field,
        sortOrder: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({ sortBy: "requestedAt", sortOrder: "desc" });
    }
  };

  const handleApprove = (record) => {
    Modal.confirm({
      title: trans("Approve Cancellation"),
      content: trans("Are you sure you want to approve this cancellation request"),
      okText: trans("Approve"),
      cancelText: trans("Cancel"),
      onOk: async () => {
        try {
          await approveRequest(record.id).unwrap();
          toast.success(trans("Cancellation request approved successfully"), {
            autoClose: 800,
            rtl: localStorage.getItem("lang") === "ar",
          });
          refetch();
        } catch (error) {
          toast.error(error?.data?.message || trans("Failed to approve cancellation request"), {
            autoClose: 800,
            rtl: localStorage.getItem("lang") === "ar",
          });
        }
      },
    });
  };

  const handleReject = (record) => {
    Modal.confirm({
      title: trans("Reject Cancellation"),
      content: trans("Are you sure you want to reject this cancellation request"),
      okText: trans("Reject"),
      cancelText: trans("Cancel"),
      onOk: async () => {
        try {
          await rejectRequest(record.id).unwrap();
          toast.success(trans("Cancellation request rejected successfully"), {
            autoClose: 800,
            rtl: localStorage.getItem("lang") === "ar",
          });
          refetch();
        } catch (error) {
          toast.error(error?.data?.message || trans("Failed to reject cancellation request"), {
            autoClose: 800,
            rtl: localStorage.getItem("lang") === "ar",
          });
        }
      },
    });
  };

  const columns = [
    {
      title: trans("Request Code"),
      key: "requestCode",
      width: 160,
      render: (_, record) => record.serviceRequest?.code || "-",
    },
    {
      title: trans("Customer Name"),
      key: "customerName",
      width: 180,
      render: (_, record) => record.serviceRequest?.poi?.name || "-",
    },
    {
      title: trans("Phone Number"),
      key: "phone",
      width: 150,
      render: (_, record) =>
        record.serviceRequest?.poi?.phoneNumber ||
        record.serviceRequest?.poi?.landline ||
        "-",
    },
    {
      title: trans("Product"),
      key: "product",
      width: 150,
      render: (_, record) => record.serviceRequest?.sku?.skuName || "-",
    },
    {
      title: trans("Requested By"),
      key: "requestedBy",
      width: 170,
      render: (_, record) => record.requestedBy?.name || "-",
    },
    {
      title: trans("Reason"),
      dataIndex: "reason",
      key: "reason",
      width: 240,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: trans("Status"),
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"} className="text-capitalize">
          {trans(status)}
        </Tag>
      ),
    },
    {
      title: trans("Requested At"),
      dataIndex: "requestedAt",
      key: "requestedAt",
      width: 190,
      sorter: true,
      render: (v) => formatDateString(v),
    },
    {
      title: trans("Actions"),
      key: "actions",
      width: 180,
      fixed: !isRTL ? "right" : undefined,
      render: (_, record) =>
        record.status === "pending" && canEdit ? (
          <Space>
            <Button type="primary" size="small" onClick={() => handleApprove(record)}>
              {trans("Approve")}
            </Button>
            <Button danger size="small" onClick={() => handleReject(record)}>
              {trans("Reject")}
            </Button>
          </Space>
        ) : (
          "-"
        ),
    },
  ];

  if (!view) {
    return <AccessDenied />;
  }

  return (
    <>
      <Helmet>
        <title>{trans("Cancelation Requests")}</title>
        <meta name="description" content="Cancelation Requests" />
      </Helmet>
      <BreadCrumb
        secButton={trans("Refresh")}
        secClick={() => refetch()}
        secLoading={isFetching}
      />
      <div className="bg-white px-3 py-2 d-flex gap-2 flex-wrap align-items-center">
        <Input
          prefix={<SearchOutlined style={{ color: "#aaa" }} />}
          placeholder={trans("Search by request code, customer or requester")}
          allowClear
          style={{ maxWidth: 320 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
        <Select
          allowClear
          placeholder={trans("Status")}
          style={{ minWidth: 160 }}
          options={STATUS_OPTIONS}
          value={statusFilter.length ? statusFilter[0] : undefined}
          onChange={(value) => {
            setStatusFilter(value ? [value] : []);
            setCurrentPage(1);
          }}
        />
      </div>
      <div className="bg-white-rounded-top mt-3 table-parent">
        <AntdTable
          columns={columns}
          loading={isFetching}
          dataSource={data?.data || []}
          pageSize={limit}
          totalItems={data?.total || 0}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setCurrentPage(1);
          }}
          onTableChange={handleTableChange}
          scrollX={true}
        />
      </div>
    </>
  );
};

export default CancelationRequests;
